import React from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { WorkoutCard } from '@/components/WorkoutCard';
import { GlassCard } from '@/components/GlassCard';
import { useApp } from '@/context/AppContext';
import { CLASSIC_TABATA, QUICK_ACCESS } from '@/data/presets';
import { calcTotalDuration, formatTime, totalRounds } from '@/models/models';
import { ChevronRight } from 'lucide-react';

const today = () =>
  new Date().toLocaleDateString('en-US', {
    weekday: 'long',
    month: 'long',
    day: 'numeric',
  });

export const Home = () => {
  const navigate = useNavigate();
  const { recents, routines } = useApp();
  const recentList = [...routines, ...recents].slice(0, 6);

  return (
    <div className="flex flex-col gap-7">
      <header>
        <p className="text-sm font-medium text-[#CBD5E1]">{today()}</p>
        <h1 className="mt-1 text-3xl font-extrabold text-white sm:text-4xl">
          Ready to train?
        </h1>
      </header>

      <WorkoutCard
        routine={CLASSIC_TABATA}
        onStart={() => navigate('/workout/classic')}
        testId="classic-tabata-card"
      />

      <section>
        <h2 className="mb-3 text-lg font-bold text-white">Quick Access</h2>
        <div className="grid grid-cols-2 gap-3">
          {QUICK_ACCESS.map((q, i) => (
            <motion.button
              key={q.id}
              initial={{ opacity: 0, y: 12 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: i * 0.05 }}
              whileTap={{ scale: 0.97 }}
              onClick={() => navigate(`/workout/${q.id}`)}
              data-testid={`quick-${q.id}`}
              className="ns-glass flex flex-col items-start gap-2 rounded-3xl p-4 text-left"
            >
              <span
                className="h-9 w-9 rounded-xl"
                style={{
                  background: `linear-gradient(135deg, ${q.accent}, ${q.accent}44)`,
                  boxShadow: `0 0 16px ${q.accent}55`,
                }}
              />
              <span className="text-sm font-bold text-white">{q.name}</span>
              <span className="text-xs text-[#CBD5E1]">{q.tag}</span>
            </motion.button>
          ))}
        </div>
      </section>

      <section>
        <div className="mb-3 flex items-center justify-between">
          <h2 className="text-lg font-bold text-white">Recent Routines</h2>
          <button
            onClick={() => navigate('/builder')}
            className="flex items-center gap-1 text-sm font-semibold text-[#38BDF8]"
            data-testid="see-builder-btn"
          >
            Builder <ChevronRight size={16} />
          </button>
        </div>
        <div className="ns-scroll-x flex gap-3 overflow-x-auto pb-2">
          {recentList.map((r) => (
            <motion.button
              key={r.id}
              whileTap={{ scale: 0.97 }}
              onClick={() => navigate(`/workout/${r.id}`)}
              data-testid={`recent-${r.id}`}
              className="min-w-[190px] shrink-0"
            >
              <GlassCard className="flex flex-col gap-2 text-left">
                <span
                  className="h-1.5 w-10 rounded-full"
                  style={{ background: r.blocks?.[0]?.color || '#38BDF8' }}
                />
                <span className="text-base font-bold text-white">{r.name}</span>
                <span className="text-xs text-[#CBD5E1] tabular-nums">
                  {formatTime(calcTotalDuration(r))} · {totalRounds(r)} rounds
                </span>
              </GlassCard>
            </motion.button>
          ))}
        </div>
      </section>
    </div>
  );
};

export default Home;
